import { PocTableBody } from 'commons/components/components-wrapper';
import { Size } from '@one/web-components';
import { OwcTableRowCustomEvent, Ref } from '@one/web-components/dist/types/components';
import { useVirtualization } from 'commons/hooks';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomTableFooterComponent } from './custom-table-footer.component';
import { CustomTableHeaderComponent } from './custom-table-header.component';
import { TableRow } from './custom-table-row.component';
import {
    CustomTableLayout,
    CustomHorizontalTableScroll,
    CustomHorizontalTableScrollContainer,
    OwcTableStyled,
} from './custom-table.style';
import {
    CustomTableConfig,
    CustomTableHeaderGroup,
    CustomTableVirtualizationProps,
    HeaderOptions,
    PaginationProps,
} from './custom-table.type';
import { TableFiltersComponentProps } from './filters/table-filters.component';
import { useComputedSelectedRowIds } from './hooks/use-computed-selected-row-ids/use-computed-selected-row-ids.hook';
import { useGroupGridTemplate } from './hooks/use-group-grid-template.hook';
import { useSyncHorizontalScroll } from './hooks/use-sync-horizontal-scroll.hook';
import { CustomTableSortingProps } from './sort/custom-table-sorting.model';

export type {
    CellOptions,
    CustomTableConfig,
    CustomTableHeaderGroup,
    CustomTableVirtualizationProps,
    FieldConfig,
    HeaderOptions,
    PaginationProps,
} from './custom-table.type';
export type { RowOptions } from './custom-table.type';
export { defaultCellOptions } from './custom-table.type';

interface CustomTableCommonProps<T extends { id: string }> {
    headerTitle: string;
    headerTitleExtraInfo?: string;
    totalElements: number;
    config: CustomTableConfig<T>;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    loading: boolean;
    tableData?: T[];
    emptyMessage: string | React.ReactElement;
    optionComponents?: React.ReactNode;
    virtualization?: CustomTableVirtualizationProps<T>;
    onEditColumns?: VoidFunction;
    headerCheckboxProps?: Partial<HTMLOwcCheckboxElement & Ref<HTMLOwcCheckboxElement>>;
}

interface CustomTableFlatProps<T extends { id: string }> {
    presentation?: { variant: 'flat' };
    showTotalElements?: boolean;
    filterProps?: TableFiltersComponentProps;
    sortingProps?: CustomTableSortingProps;
    paginationProps?: PaginationProps;
    selectedRows?: T[]; // TODO: to be removed in favor of selectedRowIds
    selectedRowIds?: Set<string>;
    clickedRow?: string;
    showSelectionColumn?: boolean;
    showGroupExpanderColumn?: boolean;
    stickyColumns?: number;
    editColumns?: boolean;
    horizontalScroll?: boolean;
    renderRow?: (data: T) => React.ReactNode;
    setSelectedRows?: React.Dispatch<React.SetStateAction<T[]>>; // TODO: to be removed in favor of setSelectedRowIds
    setSelectedRowIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
    onClickRow?: (id: string) => void;
    onSelectRow?: (id: string, event: OwcTableRowCustomEvent<boolean>) => void;
    rowOptions?: (data: T) => React.ReactElement[];
}

interface CustomTableGroupedReadOnlyProps {
    presentation: { variant: 'grouped-read-only'; headerGroups: CustomTableHeaderGroup[] };
    showTotalElements?: never;
    filterProps?: never;
    sortingProps?: never;
    paginationProps?: never;
    selectedRows?: never;
    selectedRowIds?: never;
    clickedRow?: never;
    showSelectionColumn?: never;
    showGroupExpanderColumn?: never;
    stickyColumns?: never;
    editColumns?: never;
    horizontalScroll?: never;
    renderRow?: never;
    setSelectedRows?: never;
    setSelectedRowIds?: never;
    onClickRow?: never;
    onSelectRow?: never;
    rowOptions?: never;
}

export type CustomTableComponentProps<T extends { id: string }> = CustomTableCommonProps<T> &
    (CustomTableFlatProps<T> | CustomTableGroupedReadOnlyProps);

const resolveStickyColumns = (
    isGroupedReadOnly: boolean,
    loading: boolean,
    stickyColumns: number | undefined,
    defaultStickyColumnsBeginning: number
): number => {
    if (isGroupedReadOnly) {
        return 0;
    }
    if (!loading && stickyColumns !== undefined) {
        return stickyColumns;
    }
    return defaultStickyColumnsBeginning;
};

interface GroupedReadOnlyView {
    isGroupedReadOnly: boolean;
    headerGroups: CustomTableHeaderGroup[] | undefined;
    effectiveShowTotalElements: boolean;
    effectiveShowSelectionColumn: boolean;
    effectiveShowGroupExpanderColumn: boolean;
    effectiveHorizontalScroll: boolean;
}

// The grouped-read-only variant forces several presentation flags off (or horizontal scroll on); every
// other variant passes the caller's values through. Resolving this once keeps CustomTableComponent flat.
const resolveGroupedReadOnlyView = (
    presentation:
        { variant: 'flat' } | { variant: 'grouped-read-only'; headerGroups: CustomTableHeaderGroup[] } | undefined,
    passthrough: {
        showTotalElements: boolean;
        showSelectionColumn: boolean;
        showGroupExpanderColumn: boolean;
        horizontalScroll: boolean;
    }
): GroupedReadOnlyView => {
    if (presentation?.variant === 'grouped-read-only') {
        return {
            isGroupedReadOnly: true,
            headerGroups: presentation.headerGroups,
            effectiveShowTotalElements: false,
            effectiveShowSelectionColumn: false,
            effectiveShowGroupExpanderColumn: false,
            effectiveHorizontalScroll: true,
        };
    }
    return {
        isGroupedReadOnly: false,
        headerGroups: undefined,
        effectiveShowTotalElements: passthrough.showTotalElements,
        effectiveShowSelectionColumn: passthrough.showSelectionColumn,
        effectiveShowGroupExpanderColumn: passthrough.showGroupExpanderColumn,
        effectiveHorizontalScroll: passthrough.horizontalScroll,
    };
};

export const CustomTableComponent = <T extends { id: string }>({
    headerTitle,
    headerTitleExtraInfo,
    totalElements,
    showTotalElements = true,
    config,
    scrollContainerRef,
    loading,
    tableData,
    emptyMessage,
    filterProps,
    sortingProps,
    paginationProps,
    selectedRows,
    selectedRowIds,
    clickedRow,
    showSelectionColumn = true,
    showGroupExpanderColumn = false,
    presentation,
    stickyColumns,
    editColumns = false,
    optionComponents = null,
    horizontalScroll = false,
    virtualization,
    renderRow,
    onEditColumns,
    headerCheckboxProps,
    setSelectedRows,
    setSelectedRowIds,
    onClickRow,
    onSelectRow,
    rowOptions,
}: CustomTableComponentProps<T>) => {
    const location = useLocation();
    const {
        isGroupedReadOnly,
        headerGroups,
        effectiveShowTotalElements,
        effectiveShowSelectionColumn,
        effectiveShowGroupExpanderColumn,
        effectiveHorizontalScroll,
    } = resolveGroupedReadOnlyView(presentation, {
        showTotalElements,
        showSelectionColumn,
        showGroupExpanderColumn,
        horizontalScroll,
    });

    const groupHeaderRef = useRef<HTMLDivElement>(null);
    const tableHeaderRef = useRef<HTMLOwcTableHeaderElement>(null);
    const tableBodyRef = useRef<HTMLOwcTableBodyElement>(null);
    const scrollbarRef = useRef<HTMLDivElement>(null);

    const defaultTableSize: Size = 'medium';
    const defaultHideTimeout: number = 1000;

    const defaultHeaderOptions: HeaderOptions = {
        width: 'minmax(240px, auto)',
        align: 'left',
        valign: 'middle',
        text: '',
        stickyColumnsBeginning: effectiveHorizontalScroll ? 1 : 0,
        sortable: false,
    };

    const { handleSyncHorizontalScroll } = useSyncHorizontalScroll({
        syncedScrollRefs: [groupHeaderRef, tableHeaderRef, tableBodyRef, scrollbarRef],
    });

    const fieldsToShow = useMemo(
        () =>
            config.columnsToShow
                ? config.columnsToShow
                      .filter((column) => column.visible)
                      .sort((left, right) => left.columnIndex - right.columnIndex)
                      .map((column) => config.fields.find((field) => field.name === column.field)!)
                : config.fields,
        [config.columnsToShow, config.fields]
    );

    const { enabled = false, overscan = 5, estimateRowHeight = 50, measureDeps } = virtualization || {};
    const { shouldVirtualize, virtualPaddingTop, virtualPaddingBottom, virtualRows } = useVirtualization<T>({
        disabled: !enabled,
        overscan,
        scrollContainerRef: scrollContainerRef,
        estimateRowHeight,
        data: tableData,
        measureDeps,
    });

    useGroupGridTemplate(tableBodyRef);

    useEffect(() => {
        setSelectedRowIds?.(new Set<string>());
        setSelectedRows?.([]);
    }, [location.key]);

    const isEmptyData = !tableData || tableData.length === 0;

    const dataIndicesToRender = shouldVirtualize
        ? virtualRows.map((row) => row.index)
        : (tableData?.map((_, index) => index) ?? []);

    const { computedSelectedRowIds, computedSetSelectedRowIds } = useComputedSelectedRowIds<T>({
        tableData,
        selectedRows,
        selectedRowIds,
        setSelectedRows,
        setSelectedRowIds,
    });

    const someRowsAreSelected = useMemo(
        () => !!tableData?.some((row) => computedSelectedRowIds.has(row.id)),
        [tableData, computedSelectedRowIds]
    );

    const getRowHeight = (dataIndex: number) => {
        const measureFn = typeof estimateRowHeight === 'function' ? estimateRowHeight : () => estimateRowHeight;
        return shouldVirtualize ? measureFn(dataIndex) : undefined;
    };

    const customStickyColumns = resolveStickyColumns(
        isGroupedReadOnly,
        loading,
        stickyColumns,
        defaultHeaderOptions.stickyColumnsBeginning ?? 0
    );

    return (
        <CustomTableLayout>
            <OwcTableStyled
                data-testid="custom-table"
                className={effectiveHorizontalScroll ? 'horizontal-scroll' : ''}
                size={defaultTableSize}
                scroll-hide-timeout={defaultHideTimeout}
                loading={loading}
                alignRowWithGroup
                showSelectionColumn={effectiveShowSelectionColumn}
                showGroupExpanderColumn={effectiveShowGroupExpanderColumn}
                stickyColumnBeginning={customStickyColumns}
            >
                <CustomTableHeaderComponent
                    headerRef={tableHeaderRef}
                    groupHeaderRef={groupHeaderRef}
                    scrollContainerRef={scrollContainerRef}
                    showComponentHeader={!isGroupedReadOnly}
                    headerGroups={headerGroups}
                    headerTitle={headerTitle}
                    headerTitleExtraInfo={headerTitleExtraInfo}
                    totalElements={totalElements}
                    showTotalElements={effectiveShowTotalElements}
                    config={config}
                    loading={loading}
                    emptyMessage={emptyMessage}
                    tableData={tableData ?? []}
                    filterProps={filterProps}
                    sortingProps={sortingProps}
                    selectedRowIds={computedSelectedRowIds}
                    editColumns={editColumns}
                    fieldsToShow={fieldsToShow}
                    optionComponents={optionComponents}
                    defaultHeaderOptions={defaultHeaderOptions}
                    onEditColumns={onEditColumns}
                    headerCheckboxProps={headerCheckboxProps}
                    setSelectedRowIds={computedSetSelectedRowIds}
                />
                <PocTableBody
                    id="table-body"
                    data-testid="table-body"
                    ref={tableBodyRef}
                    data-virtualization={shouldVirtualize}
                    onScroll={handleSyncHorizontalScroll}
                >
                    {!isEmptyData && fieldsToShow.length ? (
                        <>
                            {shouldVirtualize && virtualPaddingTop > 0 ? (
                                <div style={{ height: virtualPaddingTop }} />
                            ) : null}
                            {dataIndicesToRender?.map((dataIndex: number) => {
                                const data = tableData[dataIndex];
                                const rowHeight = getRowHeight(dataIndex);
                                // Expose the row height so the row-options overlay can match it
                                // without growing the row (getRowHeight is undefined when not virtualized).
                                const resolvedRowHeight =
                                    typeof estimateRowHeight === 'function'
                                        ? estimateRowHeight(dataIndex)
                                        : estimateRowHeight;
                                return (
                                    <div
                                        key={`table-row-${data.id}`}
                                        data-index={dataIndex}
                                        style={
                                            {
                                                height: rowHeight,
                                                '--custom-table-row-height': `${resolvedRowHeight}px`,
                                            } as React.CSSProperties
                                        }
                                    >
                                        {renderRow ? (
                                            renderRow(data)
                                        ) : (
                                            <TableRow
                                                data={data}
                                                isSelected={computedSelectedRowIds.has(data.id)}
                                                someRowsAreSelected={someRowsAreSelected}
                                                rowOptions={rowOptions}
                                                onClickRow={onClickRow}
                                                onSelectRow={onSelectRow}
                                                config={config}
                                                fieldsToShow={fieldsToShow}
                                                clickedRow={clickedRow}
                                                interactive={!isGroupedReadOnly}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {shouldVirtualize && virtualPaddingBottom > 0 ? (
                                <div style={{ height: virtualPaddingBottom }} />
                            ) : null}
                        </>
                    ) : (
                        <div slot="empty">{emptyMessage}</div>
                    )}
                </PocTableBody>
                <CustomHorizontalTableScrollContainer
                    ref={scrollbarRef}
                    data-testid="table-scrollbar"
                    onScroll={handleSyncHorizontalScroll}
                >
                    <CustomHorizontalTableScroll />
                </CustomHorizontalTableScrollContainer>
            </OwcTableStyled>
            {paginationProps && (
                <CustomTableFooterComponent
                    isLoading={loading}
                    totalElements={totalElements}
                    paginationProps={paginationProps}
                />
            )}
        </CustomTableLayout>
    );
};
